import React, { useState, useEffect, useRef } from "react";

import MainContainer from "../../components/MainContainer/MainContainer";
import SEO from "../../components/SEO/SEO";
import {
  Container,
  AboutHeader,
  StorySection,
  StoryContent,
  StoryText,
  StoryImage,
  ExperienceSection,
  ExperienceGrid,
  ExperienceCard,
} from "./AboutPage.styles";

import MeImg from "../../assets/images/IMG_6310.webp";

// Hook for intersection observer
const useIntersectionObserver = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const [element, setElement] = useState(null);

  useEffect(() => {
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold, rootMargin: "0px 0px -50px 0px" },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [element, threshold]);

  return [setElement, isVisible];
};

const useCounterAnimation = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    if (!countRef.current) {
      countRef.current = { start: 0, startTime: Date.now() };
    }

    const animate = () => {
      const now = Date.now();
      const elapsed = now - countRef.current.startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(easeOutCubic * end);

      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    const timer = setTimeout(() => {
      animate();
    }, 200); // Small delay before starting animation

    return () => clearTimeout(timer);
  }, [end, duration]);

  return count;
};

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = "" }) => {
  const count = useCounterAnimation(
    parseInt(end.toString().replace(/\D/g, "")),
    2000,
  );

  return (
    <span className="number">
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

const AboutPage = () => {
  const [experienceRef, experienceVisible] = useIntersectionObserver();

  const experienceStats = [
    {
      number: "5+",
      title: "Godina Iskustva",
      description: "Rada u beauty industriji",
    },
    {
      number: "10000+",
      title: "Zadovoljnih Klijenata",
      description: "Uspešno odrađenih tretmana",
    },

    {
      number: "500+",
      title: "Zadovoljnih Učenica",
      description: "Preneto znanje kroz kurseve",
    },
  ];

  return (
    <MainContainer>
      <SEO
        title="O meni"
        description="Upoznajte vlasnicu MITOVKA BeautySpot-a — iskustvo, sertifikati i priča koja stoji iza online edukacija Lash Lift i Brow Lift."
        path="/o-meni"
      />

      <Container>
        <AboutHeader>
          <h1>O Meni</h1>
          <p>Otkrijte priču iza vlasnice MITOVKA Beauty spota.</p>
        </AboutHeader>

        <StorySection>
          <StoryContent>
            <StoryText>
              <h2>Moja Priča</h2>
              <p>
                Ja sam <span className="highlight">Maša Mitov</span>, edukator,
                kreator i vlasnica <span className="highlight">MITOVKA</span>{" "}
                salona u Novom Sadu. U beauty industriju sam ušla{" "}
                <span className="highlight">2020. godine</span> i nisam ni
                slutila da će se moja ljubav prema trepavicama i obrvama
                pretvoriti iz hobija u ozbiljan biznis.
              </p>

              <p>
                Godinama sam{" "}
                <span className="highlight">skupljala informacije</span>,
                istraživala, grešila i učila. Kroz moje ruke je prošlo{" "}
                <span className="highlight">bezbroj klijenata</span>, a svoje
                znanje sam prenela na više od{" "}
                <span className="highlight">500+</span> zadovoljnih učenica koje
                su od mene učile kako uživo, tako i online.
              </p>

              <p>
                Iz tog razloga sam odlučila da svoje znanje prenesem tebi kroz
                moju novu online edukaciju za{" "}
                <span className="highlight">Lash</span> i{" "}
                <span className="highlight">Brow lift</span>.
              </p>

              <p>
                Moj put je krenuo{" "}
                <span className="highlight">2017. godine</span>, potpuno
                spontano kroz šminkanje. To je bio moj početak i prvi korak ka
                svemu što radim danas. Par godina kasnije počela sam da
                istražujem više o tretmanima Lash i Brow Lift, a svoju prvu
                edukaciju završila sam{" "}
                <span className="highlight">2020. godine</span>. Tada, sa samo{" "}
                <span className="highlight">18 godina</span>, shvatila sam da je
                to moj poziv.
              </p>

              <p>
                Uživala sam u radu sa klijentima i{" "}
                <span className="highlight">WOW efektu</span> koji ovi tretmani
                daju. Kroz godine rada sa klijentima i mnogo završenih edukacija
                i usavršavanja odlučila sam da svoje znanje prenesem na druge.
                Tako sam započela svoju ulogu kao{" "}
                <span className="highlight">edukator</span>.
              </p>

              <p>
                Iz dana u dan gradila sam svoje mesto u{" "}
                <span className="highlight">beauty industriji</span> i nisam ni
                slutila da ću danas biti tu gde jesam.
              </p>
            </StoryText>

            <StoryImage src={MeImg} alt="Profesionalni Portret" />
          </StoryContent>
        </StorySection>

        <ExperienceSection ref={experienceRef}>
          <AboutHeader $offMargin>
            <h1>Moje Iskustvo</h1>
            {/* <p>Brojevi koji govore o posvećenosti i uspešnosti.</p> */}
          </AboutHeader>

          <ExperienceGrid>
            {experienceStats.map((stat, index) => (
              <ExperienceCard
                key={index}
                $delay={experienceVisible ? index * 150 : 0}
              >
                <AnimatedCounter end={stat.number} suffix="+" />
                <h3>{stat.title}</h3>
                <p>{stat.description}</p>
              </ExperienceCard>
            ))}
          </ExperienceGrid>
        </ExperienceSection>
      </Container>
    </MainContainer>
  );
};

export default AboutPage;
